import { useOperations } from './useOperations';
import { useOperationIntent } from './useOperationIntent';
import { operationsRpc } from '@/lib/operations';
// ─────────────────────────────────────────────
// KLOTHIQ — useInventory hook
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { useAuth } from '@/contexts/AuthContext';

export const INVENTORY_KEY = 'inventory';

export interface InventorySummaryRow {
  variant_id: string;
  organization_id: string;
  sku: string;
  size: string;
  color: string | null;
  product_id: string;
  product_code: string;
  product_name: string;
  product_type_id?: string | null;
  product_type_name?: string | null;
  team: string | null;
  sleeve: string | null;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_available: number;
  weighted_avg_cost: number;
  default_price: number;
  low_stock_level: number;
  stock_status: 'in_stock' | 'low_stock' | 'out_of_stock';
}

// ── Query: full inventory summary from v_inventory_summary view ──
export function useInventory(search?: string) {
  const operations = useOperations();
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [INVENTORY_KEY, orgId, search, operations.data?.modules.catalog],
    queryFn: async (): Promise<InventorySummaryRow[]> => {
      if (operations.data?.modules.catalog === 1) {
        const all: InventorySummaryRow[] = [];
        for (let offset = 0; ; offset += 500) {
          const rows = await operationsRpc<InventorySummaryRow[]>('get_inventory_catalog_v1', {
            p_search: search || null,
            p_offset: offset,
          });
          all.push(...rows);
          if (rows.length < 500) return all;
        }
      }
      let query = supabase
        .from('v_inventory_summary')
        .select('*')
        .eq('organization_id', orgId!)
        .order('product_name')
        .order('size');

      if (search?.trim()) {
        query = query.or(
          `product_name.ilike.%${search}%,sku.ilike.%${search}%,product_code.ilike.%${search}%`,
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as InventorySummaryRow[]) ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 15,
  });
}

// ── Query: low stock only ─────────────────────────────────────
export function useLowStock() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [INVENTORY_KEY, 'low_stock', orgId],
    queryFn: async (): Promise<InventorySummaryRow[]> => {
      const { data, error } = await supabase
        .from('v_low_stock')
        .select('*')
        .eq('organization_id', orgId!);
      if (error) throw error;
      return (data as InventorySummaryRow[]) ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 30,
    refetchInterval: 60000,
  });
}

// ── Mutation: add opening stock (via RPC) ─────────────────────
export function useAddOpeningStock() {
  const queryClient = useQueryClient();
  const ops = useOperations();
  const intent = useOperationIntent('stock-control');

  return useMutation({
    mutationFn: async (params: {
      variant_id: string;
      quantity: number;
      unit_cost?: number;
      reason?: string;
    }) => {
      if (ops.data?.modules.catalog === 1) {
        const result = await operationsRpc<{ success: boolean }>('add_stock_entry_v1', {
          p_data: {
            ...params,
            unit_cost: params.unit_cost ?? 0,
            reason: params.reason || 'Manual stock entry',
            contract_version: 1,
            request_key: intent.key(params.variant_id),
          },
        });
        if (!result.success)
          throw new Error('Stock entry could not be confirmed. Retry the same details to recover.');
        intent.complete(params.variant_id);
        return result;
      }
      const { data, error } = await supabase.rpc('add_opening_stock', { p_data: params });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void refreshSaleConsumers(queryClient);
      queryClient.invalidateQueries({ queryKey: [INVENTORY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// ── Mutation: manual stock adjustment (admin only) ────────────
export function useAdjustStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      variant_id: string;
      quantity: number;
      movement_type: 'adjustment_in' | 'adjustment_out' | 'damaged' | 'stock_count';
      reason: string;
    }) => {
      const { data, error } = await supabase.rpc('adjust_stock', { p_data: params });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void refreshSaleConsumers(queryClient);
      queryClient.invalidateQueries({ queryKey: [INVENTORY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
